/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import { useRecaptchaWithFallback } from './useRecaptchaWithFallback';
import type { RecaptchaWithFallbackResult } from './recaptcha.types';

export interface WithRecaptchaProps {
    recaptcha: RecaptchaWithFallbackResult;
}

/**
 * Bridges `useRecaptchaWithFallback` into class components by injecting the
 * hook's result as a single `recaptcha` prop. Function components should use
 * the hook directly instead.
 *
 * @example
 * class MyForm extends React.Component {
 *   submit = async () => {
 *     const token = await this.props.recaptcha.getToken('my_action');
 *   };
 * }
 * export default withRecaptchaFallback(MyForm);
 */
export function withRecaptchaFallback<P extends WithRecaptchaProps>(
    WrappedComponent: React.ComponentType<P>
): React.FC<Omit<P, keyof WithRecaptchaProps>> {
    function WithRecaptchaFallback(props: Omit<P, keyof WithRecaptchaProps>) {
        const recaptcha = useRecaptchaWithFallback();
        return <WrappedComponent {...(props as P)} recaptcha={recaptcha} />;
    }

    const name = WrappedComponent.displayName || WrappedComponent.name || 'Component';
    WithRecaptchaFallback.displayName = `withRecaptchaFallback(${name})`;

    return WithRecaptchaFallback;
}
